import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { App } from 'antd';

import { useAccess } from '../auth/AccessContext.jsx';
import { ACTIVITY_EVENTS } from '../auth/idle.js';
import { pollGate } from '../businessDay/businessDay.js';
import { useLiveHub } from '../live/LiveContext.js';
import { createNotificationChannel } from './channel.js';
import { toastsFor } from './notifications.js';

/**
 * The bell's state, shared by the top bar and anything else that shows it
 * (docs/notifications/implementation-guide.md 7, 8.2):
 *
 *   unread          the badge's number (null before the first read)
 *   latest          the newest item the socket pushed, or null
 *   live            whether the socket is open (otherwise the minute fallback)
 *   items, cursor   the drawer's page, read when it opens and on a push while open
 *   loadItems({ unreadOnly, more }), markRead(ids), markAllRead(), refreshCount()
 *   drawerOpen, setDrawerOpen
 *
 * HOW IT STAYS CURRENT WITHOUT POLLING (guide rule 5). The socket at /ws
 * pushes { type: 'notification', unread, latest } the moment the
 * notifications service delivers an item, so the badge changes with no API
 * call - and so a person sitting idle is never kept signed in by the bell.
 * The connection itself, its pings, reconnects and the fallback read are
 * channel.js's, gated by the same activity rule the business day's poll uses
 * (businessDay.js pollGate): nothing is asked of the API unless the person is
 * active.
 *
 * LIVE SCREENS. The same socket carries the live screens' messages
 * ({ type: 'changed' } and { type: 'session' }, docs/live-sync/
 * implementation-plan.md): one connection per tab, never one per screen. They
 * are handed to the live hub (live/LiveProvider.jsx), which tells each
 * screen's useLiveTopic to re-read; the bell ignores them.
 *
 * Mounted inside the AccessProvider and the LiveProvider, around the Workspace
 * (Console.jsx). On sign-out the AccessProvider unmounts and the channel is
 * stopped with it.
 */
const NotificationContext = createContext({
  unread: null, latest: null, live: false, items: [], cursor: null, loading: false, error: null,
  loadItems: async () => {}, markRead: async () => {}, markAllRead: async () => {}, refreshCount: async () => {},
  drawerOpen: false, setDrawerOpen: () => {},
});

export const useNotifications = () => useContext(NotificationContext);

/** wss://<this origin>/ws?ticket=... - the socket is served by the Console's own distribution (guide 6.2). */
function socketUrl(ticket) {
  const origin = window.location.origin.replace(/^http/, 'ws');
  return origin + '/ws?ticket=' + encodeURIComponent(ticket);
}

const NotificationProvider = ({ children }) => {
  const { api } = useAccess();
  // Live screens: the socket below is the tab's only one, so it hands the hub
  // (live/LiveProvider.jsx) every live message. Null outside a LiveProvider.
  const liveHub = useLiveHub();
  const { notification } = App.useApp();
  const [unread, setUnread] = useState(null);
  const [latest, setLatest] = useState(null);
  const [live, setLive] = useState(false);
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [unreadOnly, setUnreadOnly] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerOpenRef = useRef(false);
  drawerOpenRef.current = drawerOpen;
  const lastActivity = useRef(Date.now());

  const refreshCount = useCallback(async () => {
    try {
      const { unread: n } = await api.notifications.unreadCount();
      setUnread(n);
    } catch {
      // The badge keeps its last number; the next push or tick corrects it.
    }
  }, [api]);

  /** Read the drawer's page: the first, or the next (more). */
  const loadItems = useCallback(async ({ unreadOnly: only = unreadOnly, more = false } = {}) => {
    setLoading(true);
    setError(null);
    setUnreadOnly(only);
    try {
      const page = await api.notifications.list({ limit: 20, ...(only ? { unread: 1 } : {}), ...(more && cursor ? { cursor } : {}) });
      setItems((prev) => (more ? [...prev, ...page.items] : page.items));
      setCursor(page.nextCursor ?? null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api, cursor, unreadOnly]);

  const markRead = useCallback(async (ids) => {
    if (!ids?.length) return;
    try {
      const out = await api.notifications.markRead(ids);
      setUnread(out.unread);
      const at = new Date().toISOString();
      setItems((prev) => prev.map((i) => (ids.includes(i.id) ? { ...i, readAt: i.readAt ?? at } : i)));
    } catch (e) {
      setError(e.message);
    }
  }, [api]);

  const markAllRead = useCallback(async () => {
    try {
      await api.notifications.markAllRead();
      setUnread(0);
      const at = new Date().toISOString();
      setItems((prev) => prev.map((i) => ({ ...i, readAt: i.readAt ?? at })));
    } catch (e) {
      setError(e.message);
    }
  }, [api]);

  // The channel: one socket per tab, for the life of the signed-in Workspace.
  useEffect(() => {
    const isActive = () => pollGate({ nowMs: Date.now(), lastActivityMs: lastActivity.current, hidden: document.hidden === true }).open;
    const channel = createNotificationChannel({
      requestTicket: () => api.notifications.socketTicket(),
      openSocket: (ticket) => new WebSocket(socketUrl(ticket)),
      onConnected: (info) => {
        refreshCount();
        // Live screens: after a reconnect that followed a real outage, every live screen re-reads once
        // (hints sent while it was down are gone); a routine few-second reconnect re-reads nothing (live/hub.js rule 3).
        liveHub?.connected(info);
      },
      onFallbackTick: () => refreshCount(),
      onState: (s) => setLive(s === 'OPEN'),
      isActive,
      onPush: (message) => {
        // Live screens: 'changed' and 'session' messages are the hub's (live/hub.js); the bell's are below.
        if (message.type !== 'notification') {
          liveHub?.receive(message);
          return;
        }
        if (typeof message.unread === 'number') setUnread(message.unread);
        if (message.latest) {
          setLatest(message.latest);
          // Something went wrong (not routine work): a short toast as well as the badge.
          if (toastsFor(message.latest.severity)) {
            notification.open({ message: message.latest.title, placement: 'topRight', duration: 6 });
          }
          // The drawer is open: show the new item now (the person is looking, so this read is activity anyway).
          if (drawerOpenRef.current) loadItems();
        }
      },
    });
    const onActivity = () => {
      lastActivity.current = Date.now();
      channel.activity();
    };
    ACTIVITY_EVENTS.forEach((name) => window.addEventListener(name, onActivity, { passive: true }));
    channel.start();
    return () => {
      channel.stop();
      ACTIVITY_EVENTS.forEach((name) => window.removeEventListener(name, onActivity));
    };
    // The channel lives as long as the signed-in api (and its live hub); loadItems and refreshCount are read through refs of the moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, liveHub]);

  const value = useMemo(() => ({
    unread, latest, live, items, cursor, loading, error, unreadOnly,
    loadItems, markRead, markAllRead, refreshCount, drawerOpen, setDrawerOpen,
  }), [unread, latest, live, items, cursor, loading, error, unreadOnly, loadItems, markRead, markAllRead, refreshCount, drawerOpen]);

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export default NotificationProvider;
